import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { misc } from '../services/api';
import { TrendingUp, Users, Sparkles, Loader2, CalendarClock, Rocket } from 'lucide-react';

export default function Funds() {
  const [f, setF] = useState(null);
  useEffect(() => { misc.funds().then(setF).catch(() => setF({})); }, []);
  if (!f) return <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading...</div>;

  const capital = f.capitalUsd || 0;
  const community = f.communityUsd || 0;
  const youth = f.youthUsd || 0;
  const total = capital + community + youth;
  const isCapitalPhase = f.allocationPhase !== 'split';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Community Impact Funds</h1>
        <p className="text-gray-400 text-sm">Real-time tracking across the Capital, Community, and Young Leadership funds.</p>
      </div>

      {/* EARLY ACTIVATION BANNER */}
      <div className="pbb-card p-6 border border-green-500/30 bg-green-500/5" data-testid="funds-activation-banner">
        <div className="flex items-start gap-3">
          <CalendarClock className="text-green-400 shrink-0 mt-0.5" size={22}/>
          <div>
            <div className="font-semibold text-green-300">Community &amp; Young Leadership funds activate April 1, 2027</div>
            <p className="text-sm text-gray-300 mt-1">From today through March 31, 2027, 100% of C.A.N. Auction Store funds go to the Promote Black Business LLC Capital Fund to operate soft-launch promotions and prepare for the April 1, 2027 launch. On April 1, 2027, 100% of proceeds are split 50/50 between the Community Fund and the Young Leadership Fund.</p>
            <Link to="/signup" data-testid="funds-cta" className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-600 hover:bg-green-500 text-white text-xs font-semibold">Register Free</Link>
          </div>
        </div>
      </div>

      <div className="pbb-card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-400"><TrendingUp size={20}/><span className="font-semibold">Total Raised (all funds)</span></div>
          <div className="text-xs text-gray-400">{isCapitalPhase ? 'Capital phase · through Mar 31, 2027' : 'Community + Young Leadership · 50/50 split'}</div>
        </div>
        <div className="text-4xl font-bold text-green-400 mt-2" data-testid="funds-total">${total.toFixed(2)}</div>
        <div className="text-xs text-gray-500 mt-2">Live tracker — updates the moment the next bid clears.</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`pbb-card p-6 ${isCapitalPhase ? 'ring-2 ring-yellow-500/40 bg-yellow-500/5' : ''}`} data-testid="capital-fund-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-yellow-300"><Rocket size={18}/><span className="font-semibold">Capital Fund</span></div>
            {isCapitalPhase && <span className="text-[10px] uppercase tracking-wider bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded-full">Active</span>}
          </div>
          <div className="text-3xl font-bold mt-2 text-yellow-300" data-testid="capital-fund-amount">${capital.toFixed(2)}</div>
          <p className="text-sm text-gray-400 mt-2">Operates soft-launch promotions and prepares the platform for the April 1, 2027 launch.</p>
          <div className="text-[11px] text-yellow-300/80 mt-3">100% of proceeds until Mar 31, 2027.</div>
        </div>

        <div className={`pbb-card p-6 ${!isCapitalPhase ? 'ring-2 ring-green-500/40 bg-green-500/5' : ''}`} data-testid="community-fund-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-400"><Users size={18}/><span className="font-semibold">Community Fund</span></div>
            {!isCapitalPhase && <span className="text-[10px] uppercase tracking-wider bg-green-500/20 text-green-300 px-2 py-0.5 rounded-full">Active</span>}
          </div>
          <div className="text-3xl font-bold mt-2" data-testid="community-fund-amount">${community.toFixed(2)}</div>
          <p className="text-sm text-gray-400 mt-2">Builds infrastructure, funds Black-owned business grants, and creates jobs for the community.</p>
          <div className="text-[11px] text-green-300 mt-3">Activates April 1, 2027 (50% of C.A.N. proceeds).</div>
        </div>

        <div className={`pbb-card p-6 ${!isCapitalPhase ? 'ring-2 ring-green-500/40 bg-green-500/5' : ''}`} data-testid="youth-fund-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-400"><Sparkles size={18}/><span className="font-semibold">Young Leadership Fund</span></div>
            {!isCapitalPhase && <span className="text-[10px] uppercase tracking-wider bg-green-500/20 text-green-300 px-2 py-0.5 rounded-full">Active</span>}
          </div>
          <div className="text-3xl font-bold mt-2" data-testid="youth-fund-amount">${youth.toFixed(2)}</div>
          <p className="text-sm text-gray-400 mt-2">Scholarships, mentorship, and career development for the next generation of Black leaders.</p>
          <div className="text-[11px] text-green-300 mt-3">Activates April 1, 2027 (50% of C.A.N. proceeds).</div>
        </div>
      </div>

      <div className="pbb-card p-6">
        <h3 className="font-semibold mb-2">How it works</h3>
        <ul className="text-sm text-gray-300 space-y-1 list-disc pl-5">
          <li>From today through March 31, 2027, 100% of C.A.N. Auction Store funds go to the Promote Black Business LLC Capital Fund to operate soft-launch promotions and prepare for the April 1, 2027 launch.</li>
          <li>Beginning April 1, 2027, 100% of C.A.N. Auction Store proceeds are split 50/50 between the Community Fund and the Young Leadership Fund.</li>
          <li>Every bid is a public act of support — numbers update live once the store activates.</li>
        </ul>
      </div>
    </div>
  );
}
